import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

/**
 * Holds navigation away from a page while `when` is true, so it can ask before
 * throwing away unsaved work.
 *
 * - In-app navigation (a sidebar link, the command palette, any navigate()) is
 *   held, and `blocked` turns true: show a confirmation, then call `proceed` or
 *   `cancel`.
 * - Closing or reloading the tab gets the browser's own prompt (beforeunload).
 *
 * The app runs on <BrowserRouter>, not a data router, so react-router's
 * useBlocker is not available — it throws outside one. This wraps the router's
 * navigator instead: push and replace are what every <Link> and navigate() call
 * goes through, and both read them at call time. The browser's Back button does
 * not go through them, and a popstate cannot be cancelled, so that one path is
 * not guarded.
 *
 * `bypass(fn)` runs a navigation past the guard, for the one a page makes itself
 * once the work is safe — moving to a record's own URL after creating it.
 */
export default function useUnsavedChangesGuard(when) {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const [held, setHeld] = useState(null);
    const bypassRef = useRef(false);

    useEffect(() => {
        if (!when) return undefined;

        const { push, replace } = navigator;
        const hold = (original) => (...args) => {
            if (bypassRef.current) {
                original.apply(navigator, args);
                return;
            }
            setHeld({ go: () => original.apply(navigator, args) });
        };
        navigator.push = hold(push);
        navigator.replace = hold(replace);

        return () => {
            navigator.push = push;
            navigator.replace = replace;
        };
    }, [navigator, when]);

    useEffect(() => {
        if (!when) return undefined;

        const onBeforeUnload = (event) => {
            event.preventDefault();
            // Still how Chrome is told to prompt.
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [when]);

    const proceed = useCallback(() => {
        setHeld(null);
        held?.go();
    }, [held]);

    const cancel = useCallback(() => setHeld(null), []);

    const bypass = useCallback((navigate) => {
        bypassRef.current = true;
        try {
            navigate();
        } finally {
            bypassRef.current = false;
        }
    }, []);

    return { blocked: Boolean(held), proceed, cancel, bypass };
}
